import { useEffect } from "react";
import { Crown, Skull, Award } from "lucide-react";

/**
 * Big red 5-second splash triggered when a World Boss falls. Displays the
 * top-3 damage contributors with bars, highlights the caller ("YOU") in
 * gold, and pulses a dying-boss silhouette in the background.
 *
 * Rendered by Game.jsx (like VictorySplash) so it survives when the world
 * boss view unmounts.
 */
export default function BossKillCinematic({ report, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 5200);
    return () => clearTimeout(t);
  }, [onDone]);

  if (!report) return null;

  const maxDmg = Math.max(1, ...(report.top3 || []).map(t => t.damage));
  const TIER_ICON = [Crown, Award, Skull];
  const TIER_COLOR = ["#E5C34A", "#B0ADA5", "#8A5A2B"];

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col items-center justify-center overflow-hidden"
      data-testid="boss-kill-cinematic"
      onClick={onDone}
      style={{
        background:
          "radial-gradient(60% 55% at 50% 45%, rgba(138,3,3,0.55), rgba(5,5,5,0.98)), " +
          "radial-gradient(60% 60% at 50% 100%, rgba(229,90,0,0.35), transparent 70%)",
      }}
    >
      {/* Blood spatter overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(3px 3px at 20% 30%, rgba(138,3,3,0.45), transparent 60%), " +
            "radial-gradient(3px 3px at 80% 15%, rgba(138,3,3,0.45), transparent 60%), " +
            "radial-gradient(3px 3px at 65% 70%, rgba(138,3,3,0.45), transparent 60%), " +
            "radial-gradient(3px 3px at 30% 80%, rgba(138,3,3,0.45), transparent 60%), " +
            "radial-gradient(2px 2px at 50% 45%, rgba(138,3,3,0.6), transparent 60%)",
          filter: "blur(0.5px)",
        }}
      />

      {/* Ember rain */}
      {[...Array(12)].map((_, i) => (
        <span
          key={i}
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            left: `${(i * 8.3 + 5) % 100}%`,
            top: "-20px",
            width: 4, height: 4,
            background: "#E55A00",
            borderRadius: "50%",
            boxShadow: "0 0 6px #E55A00",
            animation: `boss-ember-rain ${4 + (i % 3)}s ${(i * 0.3).toFixed(1)}s ease-in infinite`,
          }}
        />
      ))}

      <div className="relative flex flex-col items-center gap-4 max-w-[600px] w-[90%]">
        <div
          className="font-heading tracking-[0.5em] uppercase text-[#8A0303] text-xs"
          style={{ animation: "boss-cinematic-slide 0.6s ease-out both" }}
        >
          A World Boss Has Fallen
        </div>
        <div
          data-testid="boss-cinematic-name"
          className="font-heading tracking-[0.3em] uppercase text-[#E55A00] text-3xl text-center"
          style={{
            textShadow: "0 0 18px #8A0303, 0 0 6px #8A0303, 0 0 2px #000",
            animation: "boss-cinematic-name 1.4s ease-out both",
          }}
        >
          {report.boss_name}
        </div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-[#8c8c8c]">
          {report.contributors_count} champions bled it
        </div>

        {/* Top 3 damage bars */}
        <div className="w-full mt-2 flex flex-col gap-2" data-testid="boss-cinematic-top3">
          {(report.top3 || []).map((c, idx) => {
            const Icon = TIER_ICON[idx] || Skull;
            const tone = TIER_COLOR[idx] || "#666";
            return (
              <div
                key={c.user_id}
                data-testid={`boss-cinematic-rank-${idx + 1}`}
                className="flex items-center gap-3 p-2 border"
                style={{
                  borderColor: c.is_you ? "#E5C34A" : "#333",
                  background: c.is_you ? "rgba(229,195,74,0.08)" : "rgba(0,0,0,0.5)",
                  animation: `boss-cinematic-rank 0.7s ${(0.6 + idx * 0.25).toFixed(2)}s ease-out both`,
                  opacity: 0,
                }}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center shrink-0 border"
                  style={{ borderColor: tone, background: `${tone}18` }}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: tone }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-sm tracking-widest uppercase text-[#E2DFD8] truncate">
                      {c.name}
                    </span>
                    {c.is_you && (
                      <span
                        className="font-heading text-[9px] tracking-widest uppercase px-1"
                        style={{ background: "#E5C34A", color: "#050505" }}
                        data-testid="boss-cinematic-you-badge"
                      >
                        You
                      </span>
                    )}
                    <span className="ml-auto font-mono text-[10px] tracking-widest text-[#B0ADA5]">
                      #{c.rank}
                    </span>
                  </div>
                  <div className="vbar mt-1 h-2">
                    <span style={{ width: `${(c.damage / maxDmg) * 100}%`, background: tone }} />
                  </div>
                  <div className="font-mono text-[10px] text-[#8c8c8c] tracking-widest uppercase mt-0.5">
                    {c.damage.toLocaleString()} dmg · {c.pct}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 font-mono text-[10px] tracking-widest text-[#666] uppercase animate-pulse">
          Tap anywhere to continue
        </div>
      </div>

      <style>{`
        @keyframes boss-cinematic-slide {
          from { opacity: 0; letter-spacing: 0.2em; }
          to   { opacity: 1; letter-spacing: 0.5em; }
        }
        @keyframes boss-cinematic-name {
          0%   { opacity: 0; transform: scale(0.6); letter-spacing: 0.1em; filter: blur(6px); }
          40%  { opacity: 1; transform: scale(1.06); letter-spacing: 0.3em; filter: blur(0); }
          100% { opacity: 1; transform: scale(1); letter-spacing: 0.3em; filter: blur(0); }
        }
        @keyframes boss-cinematic-rank {
          from { opacity: 0; transform: translateX(-20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes boss-ember-rain {
          0%   { transform: translateY(0); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 0.7; }
          100% { transform: translateY(110vh); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
